import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose, DrawerFooter, DrawerDescription } from './ui/drawer';
import { Card, CardContent } from './ui/card';

interface WicketRecordingProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (wicketDetails: {
    dismissalType: string;
    batterOut: string;
    bowler: string;
    fielder?: string;
    runsBeforeWicket?: {
      runs: number;
      type: 'batting' | 'leg_bye' | 'bye' | 'wide' | 'no_ball';
    };
  }) => void;
  currentBatter1: { name: string; onStrike: boolean };
  currentBatter2: { name: string; onStrike: boolean };
  currentBowler: string;
  battingTeamPlayers: string[];
  bowlingTeamPlayers: string[];
}

const dismissalTypes = [
  { value: 'bowled', label: 'Bowled', requiresFielder: false },
  { value: 'caught', label: 'Caught', requiresFielder: true },
  { value: 'lbw', label: 'LBW', requiresFielder: false },
  { value: 'run_out', label: 'Run Out', requiresFielder: true },
  { value: 'stumped', label: 'Stumped', requiresFielder: true },
  { value: 'hit_wicket', label: 'Hit Wicket', requiresFielder: false },
  { value: 'caught_behind', label: 'Caught Behind', requiresFielder: false },
  { value: 'handled_ball', label: 'Handled Ball', requiresFielder: false },
  { value: 'obstructing_field', label: 'Obstructing Field', requiresFielder: false },
  { value: 'timed_out', label: 'Timed Out', requiresFielder: false },
  { value: 'retired_hurt', label: 'Retired Hurt', requiresFielder: false },
  { value: 'retired_out', label: 'Retired Out', requiresFielder: false },
];

const runOptions = [
  { value: 0, label: '0 runs' },
  { value: 1, label: '1 run' },
  { value: 2, label: '2 runs' },
  { value: 3, label: '3 runs' },
  { value: 4, label: '4 runs' },
  { value: 5, label: '5 runs' },
  { value: 6, label: '6 runs' },
];

const runTypeOptions = [
  { value: 'batting', label: 'Runs (batting)' },
  { value: 'leg_bye', label: 'Leg bye' },
  { value: 'bye', label: 'Bye' },
  { value: 'wide', label: 'Wide' },
  { value: 'no_ball', label: 'No ball' },
];

export function WicketRecording({
  isOpen,
  onClose,
  onConfirm,
  currentBatter1,
  currentBatter2,
  currentBowler,
  battingTeamPlayers,
  bowlingTeamPlayers
}: WicketRecordingProps) {
  const [dismissalType, setDismissalType] = useState('');
  const [batterOut, setBatterOut] = useState('');
  const [bowler, setBowler] = useState('');
  const [fielder, setFieldder] = useState('');
  
  // Run out specific fields
  const [runsBeforeWicket, setRunsBeforeWicket] = useState(0);
  const [runType, setRunType] = useState<'batting' | 'leg_bye' | 'bye' | 'wide' | 'no_ball'>('batting');

  const selectedDismissal = dismissalTypes.find(d => d.value === dismissalType);
  const requiresFielder = selectedDismissal?.requiresFielder || false;
  const isRunOut = dismissalType === 'run_out';

  const handleConfirm = () => {
    if (!dismissalType || !batterOut || !bowler) return;
    
    const wicketDetails = {
      dismissalType,
      batterOut,
      bowler,
      ...(requiresFielder && fielder ? { fielder } : {}),
      ...(isRunOut && runsBeforeWicket > 0 ? { 
        runsBeforeWicket: { 
          runs: runsBeforeWicket, 
          type: runType 
        } 
      } : {})
    };
    
    onConfirm(wicketDetails);
    handleClose();
  };

  const handleClose = () => {
    setDismissalType('');
    setBatterOut('');
    setBowler('');
    setFieldder('');
    setRunsBeforeWicket(0);
    setRunType('batting');
    onClose();
  };

  const isValid = dismissalType && batterOut && bowler && (!requiresFielder || fielder);

  // Set defaults when drawer opens
  useEffect(() => {
    if (isOpen) {
      // Set current bowler as default
      setBowler(currentBowler);
      
      // Auto-select the on-strike batter by default
      if (!batterOut) {
        if (currentBatter1.onStrike) {
          setBatterOut(currentBatter1.name);
        } else if (currentBatter2.onStrike) {
          setBatterOut(currentBatter2.name);
        }
      }
    }
  }, [isOpen, currentBowler, currentBatter1, currentBatter2, batterOut]);

  const formatWicketSummary = () => {
    let summary = `${batterOut} ${selectedDismissal?.label.toLowerCase()}`;
    
    if (dismissalType === 'caught' && fielder) {
      summary += ` by ${fielder}`;
    } else if (dismissalType === 'run_out' && fielder) {
      summary += ` by ${fielder}`;
    } else if (dismissalType === 'stumped' && fielder) {
      summary += ` by ${fielder}`;
    } else if (dismissalType === 'caught_behind') {
      summary += ' by wicket-keeper';
    }
    
    if (['bowled', 'lbw', 'caught', 'caught_behind'].includes(dismissalType)) {
      summary += ` b ${bowler}`;
    }

    // Add runs before wicket for run outs
    if (isRunOut && runsBeforeWicket > 0) {
      const runTypeLabel = runTypeOptions.find(r => r.value === runType)?.label.toLowerCase();
      summary += ` (${runsBeforeWicket} ${runTypeLabel} before dismissal)`;
    }

    return summary;
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="max-h-[95vh] flex flex-col">
        <div className="mx-auto w-full max-w-sm flex flex-col min-h-0">
          <DrawerHeader className="flex-shrink-0">
            <DrawerTitle>Record Wicket</DrawerTitle>
            <DrawerDescription>
              Select the dismissal type, batter out, and other details for the wicket.
            </DrawerDescription>
          </DrawerHeader>
          
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="flex flex-col gap-4">
              
              {/* Dismissal Type */}
              <div className="flex flex-col gap-2">
                <Label>How was the batter dismissed?</Label>
                <Select value={dismissalType} onValueChange={setDismissalType}>
                  <SelectTrigger className="text-base">
                    <SelectValue placeholder="Select dismissal type" />
                  </SelectTrigger>
                  <SelectContent>
                    {dismissalTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Batter Out */}
              <div className="flex flex-col gap-2">
                <Label>Which batter was dismissed?</Label>
                <Select value={batterOut} onValueChange={setBatterOut}>
                  <SelectTrigger className="text-base">
                    <SelectValue placeholder="Select batter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={currentBatter1.name}>
                      {currentBatter1.name} {currentBatter1.onStrike ? '(on strike)' : ''}
                    </SelectItem>
                    <SelectItem value={currentBatter2.name}>
                      {currentBatter2.name} {currentBatter2.onStrike ? '(on strike)' : ''}
                    </SelectItem>
                    {battingTeamPlayers
                      .filter(player => player !== currentBatter1.name && player !== currentBatter2.name)
                      .map((player) => (
                        <SelectItem key={player} value={player}>
                          {player}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              {/* Bowler */}
              <div className="flex flex-col gap-2">
                <Label>Bowler</Label>
                <Select value={bowler} onValueChange={setBowler}>
                  <SelectTrigger className="text-base">
                    <SelectValue placeholder="Select bowler" />
                  </SelectTrigger>
                  <SelectContent>
                    {bowlingTeamPlayers.map((player) => (
                      <SelectItem key={player} value={player}>
                        {player} {player === currentBowler ? '(current)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fielder (if required) */}
              {requiresFielder && (
                <div className="flex flex-col gap-2">
                  <Label>
                    {dismissalType === 'caught' ? 'Caught by' : 
                     dismissalType === 'run_out' ? 'Run out by' :
                     dismissalType === 'stumped' ? 'Stumped by' : 'Fielder'}
                  </Label>
                  <Select value={fielder} onValueChange={setFieldder}>
                    <SelectTrigger className="text-base">
                      <SelectValue placeholder="Select fielder" />
                    </SelectTrigger>
                    <SelectContent>
                      {bowlingTeamPlayers.map((player) => (
                        <SelectItem key={player} value={player}>
                          {player}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Run Out Details */}
              {isRunOut && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-sm mb-3 text-orange-800">Runs before run out</h4>
                    <div className="flex flex-col gap-3">
                      
                      {/* Number of runs */}
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm">How many runs were scored?</Label>
                        <Select value={runsBeforeWicket.toString()} onValueChange={(value) => setRunsBeforeWicket(parseInt(value))}>
                          <SelectTrigger className="text-base">
                            <SelectValue placeholder="Select runs" />
                          </SelectTrigger>
                          <SelectContent>
                            {runOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value.toString()}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Type of runs (if > 0) */}
                      {runsBeforeWicket > 0 && (
                        <div className="flex flex-col gap-2">
                          <Label className="text-sm">How were the runs scored?</Label>
                          <Select value={runType} onValueChange={(value: 'batting' | 'leg_bye' | 'bye' | 'wide' | 'no_ball') => setRunType(value)}>
                            <SelectTrigger className="text-base">
                              <SelectValue placeholder="Select run type" />
                            </SelectTrigger>
                            <SelectContent>
                              {runTypeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Examples */}
                      <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                        <strong>Examples:</strong>
                        <ul className="mt-1 space-y-1">
                          <li>• Leg bye taken, ran 2, run out attempting 3rd</li>
                          <li>• No ball, batter runs 1, run out at non-striker's end</li>
                          <li>• Batter runs 2, run out attempting 3rd run</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary */}
              {dismissalType && batterOut && bowler && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-medium text-sm mb-1">Wicket Summary</h4>
                  <p className="text-sm text-gray-600">
                    {formatWicketSummary()}
                  </p>
                </div>
              )}

              {/* Bottom padding for safe scrolling */}
              <div className="h-4"></div>
            </div>
          </div>

          <DrawerFooter className="flex-shrink-0">
            <Button 
              onClick={handleConfirm} 
              disabled={!isValid}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirm Wicket
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}